import React, { useState, useEffect, useRef } from 'react';
import { ApiService } from '../services/apiService';
import './BiometricSetup.css';
import { authenticateWithBiometric } from '../utils/biometricAuth';
import { getDeviceInfo, generateDeviceSignature } from '../utils/deviceSignature';

interface BiometricSetupProps {
  cardId: string;
  onSuccess: () => void;
  onSkip: () => void;
}

interface AuthenticationData {
  signingMethodId: string;
  signingMethodValue: string;
}

const BiometricSetup: React.FC<BiometricSetupProps> = ({ cardId, onSuccess, onSkip }) => {
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [biometricStatus, setBiometricStatus] = useState<any>(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [showAuthChoiceModal, setShowAuthChoiceModal] = useState(false);
  const [pin, setPin] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  /** Set when PIN modal opens so submit does not await APIs before WebAuthn (required on iOS Safari). */
  const [pinSigningMethodId, setPinSigningMethodId] = useState<string | null>(null);
  const webAuthnAbortRef = useRef<AbortController | null>(null);
  const webAuthnWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const webAuthnTimedOutRef = useRef(false);

  useEffect(() => {
    checkBiometricSupport();
    checkBiometricStatus();
    // Clear any previous errors when component mounts
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear errors when biometric status changes
  useEffect(() => {
    if (biometricStatus) {
      setError(null);
    }
  }, [biometricStatus]);

  useEffect(() => {
    return () => {
      webAuthnAbortRef.current?.abort();
      if (webAuthnWatchdogRef.current) {
        clearTimeout(webAuthnWatchdogRef.current);
        webAuthnWatchdogRef.current = null;
      }
    };
  }, []);

  const clearWebAuthnWatchdog = () => {
    if (webAuthnWatchdogRef.current) {
      clearTimeout(webAuthnWatchdogRef.current);
      webAuthnWatchdogRef.current = null;
    }
  };

  const checkBiometricSupport = () => {
    if ('credentials' in navigator && 'PublicKeyCredential' in window) {
      if (PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) {
        PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
          .then((available) => {
            setIsSupported(available);
          });
      }
    }
  };

  const checkBiometricStatus = async () => {
    try {
      const response = await ApiService.checkBiometricStatus(cardId);
      if (response.success) {
        setBiometricStatus(response.data);
      }
    } catch (error) {
      console.error('Error checking biometric status:', error);
    }
  };

  const setupBiometric = async () => {
    // Clear any previous errors
    setError(null);

    if (!biometricStatus?.canAddMore) {
      setError('Maximum number of devices reached');
      return;
    }

    setIsSettingUp(false);

    // Check if user already has biometrics set up
    if (biometricStatus?.hasBiometric && biometricStatus?.deviceCount > 0) {
      // User already has biometrics - show choice between PIN and existing biometrics
      setShowAuthChoiceModal(true);
    } else {
      // First time setup - proceed with PIN authentication
      await proceedWithPinAuth();
    }
  };

  const proceedWithPinAuth = async () => {
    // Close auth choice modal first to avoid modal overlap
    setShowAuthChoiceModal(false);
    setIsSettingUp(true);
    setError(null);

    try {
      // Get the user's signing methods to determine authentication
      const signingMethodsResponse = await ApiService.getUserSigningMethods(cardId);
      
      if (!signingMethodsResponse.success || !signingMethodsResponse.data?.signingMethods) {
        throw new Error('Failed to get user authentication methods');
      }

      const signingMethods = signingMethodsResponse.data.signingMethods;
      const pinMethod = signingMethods.find((method: any) => method.type === 'PIN');
      
      if (!pinMethod) {
        throw new Error('No PIN method found. Please activate your card first.');
      }

      setPinSigningMethodId(pinMethod.id);
      // Show PIN input modal
      setShowPinModal(true);
      setIsSettingUp(false);
      
    } catch (error: any) {
      console.error('Biometric setup error:', error);
      if (error.message && error.message !== 'Failed to setup biometric authentication') {
        setError(error.message);
      } else {
        setError('Biometric setup failed. Please try again or skip this step.');
      }
      setIsSettingUp(false);
    }
  };

  const proceedWithBiometricAuth = async () => {
    setIsSettingUp(true);
    setError(null);

    try {
      // Check if user has existing biometric on this device
      const existingBiometric = await checkExistingBiometricOnDevice();
      
      if (existingBiometric) {
        // Use existing biometric for authentication
        const authentication: AuthenticationData = {
          signingMethodId: existingBiometric.id,
          signingMethodValue: await authenticateWithExistingBiometric(existingBiometric)
        };
        
        // Continue with biometric setup
        await completeBiometricSetup(authentication);
      } else {
        // No existing biometric on this device, fall back to PIN
        setError('No existing biometric found on this device. Please use your PIN instead.');
        setShowAuthChoiceModal(false);
        await proceedWithPinAuth();
      }
      
    } catch (error: any) {
      console.error('Biometric authentication error:', error);
      setError('Biometric authentication failed. Please use your PIN instead.');
      setShowAuthChoiceModal(false);
      await proceedWithPinAuth();
    }
  };

  const checkExistingBiometricOnDevice = async (): Promise<any> => {
    try {
      // Check if there's a biometric method for this device
      const deviceSignature = await generateDeviceSignature();
      
      // Get all biometric methods for this card
      const response = await ApiService.getCardBiometricMethods(cardId);
      
      if (response.success && response.data && Array.isArray(response.data)) {
        // Try to find a biometric method that matches this device's signature
        const matchingMethod = response.data.find((method: any) => 
          method.deviceSignature === deviceSignature
        );
        
        if (matchingMethod) {
          return matchingMethod;
        }
        
        // If no exact match, fall back to first available biometric
        // (user might be on a different device but can still use existing biometric)
        if (response.data.length > 0) {
          return response.data[0];
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error checking existing biometric:', error);
      return null;
    }
  };

  const authenticateWithExistingBiometric = async (biometricMethod: any): Promise<string> => {
    // Use WebAuthn authentication - it will return the credential.id which matches what was stored in Venly
    const result = await authenticateWithBiometric();
    if (result.success && result.credentialId) {
      // Venly expects the credentialId (the same value stored during creation), not the signature
      return result.credentialId;
    }
    throw new Error(result.error || 'Biometric authentication failed');
  };

  const completeBiometricSetup = async (
    authentication: AuthenticationData,
    webAuthnSignal?: AbortSignal
  ): Promise<boolean> => {
    try {
      // WebAuthn must be the first async step after the user tap (iOS Safari). Do not await getDeviceInfo first.
      const credential = await createBiometricCredential(webAuthnSignal);

      if (!credential || !('id' in credential)) {
        setError(
          'Passkey setup did not complete. If nothing appeared, try again and allow the system prompt when it shows.'
        );
        return false;
      }

      const publicKeyCredential = credential as PublicKeyCredential;
      const deviceInfo = await getDeviceInfo();

      const response = await ApiService.setupBiometricAuth(
        cardId,
        {
          deviceType: deviceInfo.deviceType,
          deviceId: deviceInfo.deviceId,
          deviceSignature: deviceInfo.deviceSignature,
          platform: deviceInfo.platform,
          deviceName: deviceInfo.deviceName,
          credentialId: publicKeyCredential.id
        },
        authentication
      );

      if (response.success) {
        onSuccess();
        return true;
      }
      const errorMsg =
        (response as any).error || response.message || 'Failed to setup biometric authentication';
      setError(errorMsg);
      return false;
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        if (webAuthnTimedOutRef.current) {
          setError(
            'Passkey prompt did not appear in time. On Android Chrome, dismiss system prompts with Back and try again.'
          );
        } else {
          setError('Passkey setup was cancelled. You can try again when ready.');
        }
        return false;
      }
      if (error?.name === 'NotAllowedError') {
        setError('Passkey setup was cancelled. You can try again when ready.');
        return false;
      }
      console.error('Biometric setup error:', error);
      const errorMessage = error.error || error.message;
      if (errorMessage && errorMessage !== 'Failed to setup biometric authentication') {
        setError(errorMessage);
      } else {
        setError('Biometric setup failed. Please try again or skip this step.');
      }
      return false;
    } finally {
      setIsSettingUp(false);
    }
  };

  const handlePinSubmit = async () => {
    if (pin.length !== 6) {
      setError('Please enter a 6-digit PIN');
      return;
    }

    if (!pinSigningMethodId) {
      setError('Setup expired. Close this dialog and tap Setup again.');
      return;
    }

    const abort = new AbortController();
    webAuthnAbortRef.current = abort;
    webAuthnTimedOutRef.current = false;
    clearWebAuthnWatchdog();
    webAuthnWatchdogRef.current = setTimeout(() => {
      webAuthnTimedOutRef.current = true;
      webAuthnAbortRef.current?.abort();
    }, 15000);

    setIsAuthenticating(true);
    setError(null);
    setShowPinModal(false);
    (document.activeElement as HTMLElement | null)?.blur?.();

    const authentication: AuthenticationData = {
      signingMethodId: pinSigningMethodId,
      signingMethodValue: pin
    };

    try {
      const ok = await completeBiometricSetup(authentication, abort.signal);
      if (!ok) {
        return;
      }
    } catch (error: any) {
      console.error('PIN authentication error:', error);
      if (error?.name !== 'AbortError') {
        setError(error.message || 'PIN authentication failed');
      }
    } finally {
      webAuthnAbortRef.current = null;
      clearWebAuthnWatchdog();
      setIsAuthenticating(false);
    }
  };

  const closePinModal = () => {
    clearWebAuthnWatchdog();
    webAuthnAbortRef.current?.abort();
    setShowPinModal(false);
    setPin('');
    setPinSigningMethodId(null);
    setError(null);
    setIsAuthenticating(false);
  };

  // Use shared device info utility
  // getDeviceInfo is now imported from deviceSignature.ts

  // Device signature and device ID generation are now handled by shared utility
  // generateDeviceSignature and generateDeviceId are imported from deviceSignature.ts

  const createBiometricCredential = async (signal?: AbortSignal) => {
    return await navigator.credentials.create({
      signal,
      publicKey: {
        challenge: new Uint8Array(32),
        rp: {
          name: "MCC App",
          id: window.location.hostname
        },
        user: {
          id: new Uint8Array(16),
          name: `user-${cardId}`,
          displayName: `Card ${cardId}`
        },
        pubKeyCredParams: [{
          type: "public-key",
          alg: -7
        }],
        authenticatorSelection: {
          authenticatorAttachment: "platform",
          userVerification: "required"
        },
        timeout: 60000
      }
    });
  };

  if (!isSupported) {
    return (
      <div className="biometric-setup">
        <div className="biometric-not-supported">
          <h3>Biometric Authentication Not Available</h3>
          <p>Your device doesn't support biometric authentication or it's not enabled.</p>
          <button className="skip-btn" onClick={onSkip}>
            Continue with PIN Only
          </button>
        </div>
      </div>
    );
  }

  if (biometricStatus && !biometricStatus.canAddMore) {
    return (
      <div className="biometric-setup">
        <div className="device-limit-reached">
          <h3>Device Limit Reached</h3>
          <p>You have already set up biometric authentication on {biometricStatus.deviceCount} devices.</p>
          <button className="skip-btn" onClick={onSkip}>
            Continue with PIN Only
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="biometric-setup">
      <div className="biometric-content">
        <div className="biometric-icon">🔐</div>
        <h3>Enhance Your Security</h3>
        <p>Add biometric authentication to your account for quick and secure access to all your cards.</p>
        
        {biometricStatus && (
          <div className="device-info">
            <span>Devices registered: {biometricStatus.deviceCount}/2</span>
          </div>
        )}

        <div className="biometric-benefits">
          <div className="benefit">
            <span className="benefit-icon">⚡</span>
            <span>Faster transactions across all cards</span>
          </div>
          <div className="benefit">
            <span className="benefit-icon">🛡️</span>
            <span>Enhanced security for your account</span>
          </div>
          <div className="benefit">
            <span className="benefit-icon">📱</span>
            <span>Convenient access on this device</span>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="biometric-actions">
          <button 
            className="setup-btn"
            onClick={setupBiometric}
            disabled={isSettingUp || !biometricStatus?.canAddMore}
          >
            {isSettingUp ? 'Setting up...' : 
             biometricStatus?.hasBiometric ? 'Add Another Biometric Device' : 'Setup Biometric Authentication'}
          </button>
          
          <button 
            className="skip-btn"
            onClick={onSkip}
            disabled={isSettingUp}
          >
            Skip for Now
          </button>
        </div>
      </div>

      {/* PIN Input Modal */}
      {showPinModal && (
        <div className="pin-modal-overlay">
          <div className="pin-modal">
            <div className="pin-modal-header">
              <h3>Enter Your PIN</h3>
              <p>Please enter your card PIN to continue with biometric setup</p>
            </div>

            <div className="pin-input-container">
              <input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="Enter 6-digit PIN"
                maxLength={6}
                className="pin-input"
                autoFocus
              />
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="pin-modal-actions">
              <button
                type="button"
                onClick={handlePinSubmit}
                disabled={isAuthenticating || pin.length !== 6}
                className="pin-submit-btn"
              >
                {isAuthenticating ? 'Verifying...' : 'Continue'}
              </button>

              <button type="button" onClick={closePinModal} className="pin-cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Authentication Choice Modal */}
      {showAuthChoiceModal && (
        <div className="pin-modal-overlay">
          <div className="pin-modal">
            <div className="pin-modal-header">
              <h3>Choose Authentication Method</h3>
              <p>How would you like to authenticate to add another biometric device?</p>
            </div>
            
            <div className="auth-choice-container">
              <button 
                onClick={proceedWithBiometricAuth}
                className="auth-choice-btn biometric-choice"
                disabled={isSettingUp}
              >
                <span className="auth-choice-icon">👆</span>
                <span className="auth-choice-text">
                  <strong>Use Existing Biometric</strong>
                  <small>Authenticate with your current biometric device</small>
                </span>
              </button>
              
              <button 
                onClick={proceedWithPinAuth}
                className="auth-choice-btn pin-choice"
                disabled={isSettingUp}
              >
                <span className="auth-choice-icon">🔢</span>
                <span className="auth-choice-text">
                  <strong>Use PIN</strong>
                  <small>Enter your 6-digit card PIN</small>
                </span>
              </button>
            </div>

            <div className="pin-modal-actions">
              <button 
                onClick={() => {
                  setShowAuthChoiceModal(false);
                  setError(null);
                }}
                className="pin-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BiometricSetup;
